import { Navigate } from 'react-router-dom'
import { useAuthStore } from '../auth.store'
import { hasRoleAccess } from '../auth.utils'
import type { RoleType } from '../auth.types'

export interface RoleProtectedRouteProps {
  children: React.ReactNode
  allowedRoles: RoleType[]
  /**
   * Optional fallback component to show when access is denied
   * If not provided, redirects to /403 (full-page error)
   */
  fallback?: React.ComponentType
  /**
   * If true, renders fallback inline instead of redirecting
   * @default false
   */
  inline?: boolean
}

/**
 * Route-level component for role-based access control
 * Use this when you want to protect routes directly in your router configuration
 * 
 * @example
 * ```tsx
 * <Route
 *   path="/finance"
 *   element={
 *     <RoleProtectedRoute allowedRoles={['finance', 'super_customer']}>
 *       <FinancePage />
 *     </RoleProtectedRoute>
 *   }
 * />
 * ```
 */
export function RoleProtectedRoute({
  children,
  allowedRoles,
  fallback: Fallback,
  inline = false,
}: RoleProtectedRouteProps) {
  const { customer } = useAuthStore()

  // If not authenticated, let ProtectedRoute handle it (should be wrapped)
  if (!customer) {
    return null
  }

  // Check if user has required role
  const hasAccess = hasRoleAccess(customer?.role, allowedRoles)

  if (!hasAccess) {
    // If inline is true and fallback is provided, render inline
    if (inline && Fallback) {
      return <Fallback />
    }
    
    // Otherwise, redirect to full-page 403 error
    return <Navigate to="/403" replace />
  }

  return <>{children}</>
}

